import * as React from "react";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import { Modal, Button, Grid } from "semantic-ui-react";
import "react-image-crop/dist/ReactCrop.css";
import "./styles.scss";
import { useEffect, useRef, useState } from "react";

interface OwnProps {
  src?: string;
  open: boolean;
  type?: string;
  cropping: boolean;

  handleSave?: (file: any, blob: Blob) => void;
  handleClose: () => void;
}

type Props = OwnProps;

const EditImageModalFC: React.FC<Props> = ({ open, src, handleSave, handleClose, cropping }) => {
  const imageRef = useRef<HTMLImageElement>(null);

  const [croppedImageUrl, setCroppedImageUrl] = useState<string | undefined>(undefined);
  const [blob, setBlob] = useState<Blob>();
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  interface AspectRatioOption {
    label: string;
    aspectX: number;
    aspectY: number;
  }

  const aspectRatioOptions: AspectRatioOption[] = [
    { label: "1:1", aspectX: 1, aspectY: 1 },
    { label: "4:5", aspectX: 4, aspectY: 5 },
    { label: "16:9", aspectX: 16, aspectY: 9 },
    { label: "1.91:1", aspectX: 1.91, aspectY: 1 },
    { label: "9:16", aspectX: 9, aspectY: 16 },
    { label: "3:4", aspectX: 3, aspectY: 4 },
  ];

  const [aspectRatio, setAspectRatio] = useState<AspectRatioOption>(aspectRatioOptions[0]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const aspect = aspectRatio.aspectX / aspectRatio.aspectY;

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
        },
        aspect,
        width,
        height
      ),
      width,
      height
    );

    setCrop(initialCrop);
  };

  const getCroppedImg = async (image: HTMLImageElement, pixelCrop: PixelCrop): Promise<{ url: string; blob: Blob }> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = Math.floor(pixelCrop.width * scaleX);
    canvas.height = Math.floor(pixelCrop.height * scaleY);

    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blobThumbnail => {
          if (!blobThumbnail) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve({
            url: window.URL.createObjectURL(blobThumbnail),
            blob: blobThumbnail,
          });
        },
        "image/png",
        1
      );
    });
  };

  const onCropComplete = async (pixelCrop: PixelCrop) => {
    if (imageRef.current && pixelCrop.width && pixelCrop.height) {
      try {
        const { url, blob: resultBlob } = await getCroppedImg(imageRef.current, pixelCrop);
        setCroppedImageUrl(url);
        setBlob(resultBlob);
      } catch (e) {
        console.error("Error generating crop preview", e);
      }
    }
  };

  useEffect(() => {
    if (imageRef.current && aspectRatio) {
      const { width, height } = imageRef.current;
      const aspect = aspectRatio.aspectX / aspectRatio.aspectY;

      const newCrop = centerCrop(
        makeAspectCrop(
          {
            unit: "%",
            width: 90,
          },
          aspect,
          width,
          height
        ),
        width,
        height
      );

      setCrop(newCrop);
    }
  }, [aspectRatio]);

  return (
    <Modal closeIcon className={"previewModal"} open={open} onClose={handleClose}>
      <Modal.Header>
        {aspectRatioOptions.map(option => (
          <Button
            data-elm-id={`editImageModalCrop${option.label}Btn`}
            key={option.label}
            onClick={() => setAspectRatio(option)}
            className={aspectRatio === option ? "aspectSelected" : "centered"}
          >
            {option.label}
          </Button>
        ))}
      </Modal.Header>
      <Modal.Content>
        <Grid>
          <Grid.Row centered={true}>
            {src && (
              <ReactCrop
                crop={crop}
                onChange={c => setCrop(c)}
                onComplete={onCropComplete}
                aspect={aspectRatio.aspectX / aspectRatio.aspectY}
                className={"reactCropContainer"}
              >
                <img
                  ref={imageRef}
                  src={src}
                  alt="Crop me"
                  onLoad={onImageLoad}
                  crossOrigin="anonymous"
                  style={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              </ReactCrop>
            )}
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Grid className={"centered mediumPadding"}>
          <Button
            data-elm-id={`editImageModalDoneBtn`}
            loading={cropping}
            disabled={!blob || !croppedImageUrl}
            onClick={() => {
              if (blob && croppedImageUrl) {
                handleSave?.(croppedImageUrl, blob);
              }
            }}
            className={"primary rounded centered"}
          >
            Done
          </Button>
        </Grid>
      </Modal.Actions>
    </Modal>
  );
};

export const EditImageModal = EditImageModalFC;
